import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { Button, Input, TextField } from "@heroui/react";
import { type FormEvent, useId, useState } from "react";
import type { Audience } from "@/content";

type Status = "idle" | "submitting" | "success" | "error";

type WaitlistFormProps = {
  /** Slightly larger controls for the hero vs. the closing CTA. */
  size?: "md" | "lg";
  /** Where the source came from — logged now, sent to the BE later. */
  source: string;
  /** Active audience from the page-level toggle — included in the signup. */
  audience: Audience;
};

/**
 * Waitlist capture. On submit we POST to `/api/waitlist`, which stores the
 * signup in KV and sends a confirmation email via Resend. The audience comes
 * from the page-level toggle so the signup reflects the visitor's choice.
 */
export function WaitlistForm({ size = "md", source, audience }: WaitlistFormProps) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const errorId = useId();

  const isBusy = status === "submitting";

  async function submit(event: FormEvent) {
    event.preventDefault();
    const value = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setStatus("error");
      return;
    }

    setStatus("submitting");
    try {
      const res = await fetch("/api/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: value, source, audience }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setStatus("success");
    } catch (err) {
      console.error("[waitlist] submit failed", err);
      setStatus("error");
    }
  }

  if (status === "success") {
    return (
      <div
        role="status"
        className="flex items-start gap-3 rounded-[var(--field-radius)] border border-[var(--success)] bg-[color-mix(in_oklch,var(--success)_10%,var(--surface))] px-5 py-4"
      >
        <CheckCircleIcon className="mt-0.5 h-6 w-6 shrink-0 text-[var(--success)]" />
        <p className="text-sm text-[var(--foreground)]">
          <strong className="font-semibold">¡Listo!</strong> Ya estás en la lista. Te escribimos
          apenas haya novedades de lanzamiento.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      noValidate
      className="flex w-full flex-col gap-3 sm:flex-row sm:items-start"
    >
      <TextField
        type="email"
        value={email}
        isInvalid={status === "error"}
        aria-label="Correo electrónico"
        {...(status === "error" ? { "aria-describedby": errorId } : {})}
        onChange={(value) => {
          setEmail(value);
          if (status === "error") setStatus("idle");
        }}
        className="flex-1"
      >
        <Input
          inputMode="email"
          autoComplete="email"
          placeholder="tu@imprenta.com"
          className={size === "lg" ? "text-base" : ""}
        />
        {status === "error" && (
          <p id={errorId} className="mt-1.5 text-sm text-[var(--danger)]">
            No pudimos sumarte. Revisá el correo o probá de nuevo en un rato.
          </p>
        )}
      </TextField>
      <Button
        type="submit"
        variant="primary"
        size={size === "lg" ? "lg" : "md"}
        isPending={isBusy}
        isDisabled={isBusy}
        className="w-full shrink-0 sm:w-auto"
      >
        {isBusy ? "Sumándote…" : "Sumate a la lista"}
      </Button>
    </form>
  );
}
